import React from 'react';
import { Box, Grid2 as Grid, Typography } from '@mui/material';
import { PROJECT_TEAM_LOCATION_LABELS, PROJECT_TEAM_TYPE_LABELS } from 'domain/shared/newProject';
import { useShowContext } from 'framework/show';
import { StaffingDataType } from 'domain/shared/staffing';
import { EnumField, FormattedTextField, TextField } from 'framework/ui/field';
import { AppIconLabel } from 'framework/ui/mui-builder';
import { DATE_FORMAT, useDateService, formatDecimal, EMPTY_VALUE_INDICATOR } from 'framework/utils';
import { IconSectionRow } from 'framework/ui/layout';
import { OPP_STATUS_LABELS } from '../shared/constants';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import PriorityHigh from '@mui/icons-material/PriorityHigh';
import { Tooltip } from 'framework/ui/mui-builder';

export const IconLabel = ({ children }: { children: React.ReactNode }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', width: 40, minWidth: 40, flexShrink: 0 }}>
    {children}
  </Box>
);

const OpportunityDashboardGeneralInfo = () => {
  const { data } = useShowContext<StaffingDataType>();
  const { team, ...projectData } = data.getData() || {};
  const { formatDate, getDaysBetween, getToday } = useDateService();

  const expectedFTE = team?.expectedStructure?.reduce((sum, member) => sum + (Number(member.requiredFte) || 0), 0);

  const expectedStartDate = projectData.engagement?.expectedStartDate;
  const daysToExpectedStart = expectedStartDate ? getDaysBetween(expectedStartDate, getToday()) : null;

  const isExpectedStartDateWarning = daysToExpectedStart !== null && daysToExpectedStart <= 14;
  const expectedStartDateValue = expectedStartDate
    ? formatDate(expectedStartDate, DATE_FORMAT.FULL_DATE)
    : EMPTY_VALUE_INDICATOR;

  return (
    <Grid container sx={{ pb: 2 }}>
      <Grid size={12}>
        <IconSectionRow colXl={6} colMd={6} sxItem={{ mb: '10px!important' }}>
          <FormattedTextField
            source={'name'}
            label=""
            format={(str: string) => <Typography color="textSecondary">{str}</Typography>}
          />
          <TextField
            value={expectedFTE ? `${formatDecimal(expectedFTE)} FTE` : EMPTY_VALUE_INDICATOR}
            label={
              <IconLabel>
                <AppIconLabel icon="group" tooltipTitle="Expected FTE" />
              </IconLabel>
            }
            mb={1}
          />
          <FormattedTextField
            label={<Typography color="textSecondary">Current state:</Typography>}
            source={'engagement.status'}
            format={(str: string) => (
              <Typography variant="subtitle1" pl={1}>
                {str ? OPP_STATUS_LABELS[str] : EMPTY_VALUE_INDICATOR}
              </Typography>
            )}
          />
          <EnumField
            source="location"
            value={team?.location}
            label={
              <IconLabel>
                <AppIconLabel icon="location" tooltipTitle="Team location" />
              </IconLabel>
            }
            options={PROJECT_TEAM_LOCATION_LABELS}
          />
          <FormattedTextField
            source={'engagement.expectedStartDate'}
            value={
              <Box
                display="inline-flex"
                alignItems="center"
                color={isExpectedStartDateWarning ? 'error.main' : 'text.main'}
              >
                <Typography color="inherit">{expectedStartDateValue}</Typography>
                {isExpectedStartDateWarning ? <PriorityHigh color="error" sx={{ fontSize: 16 }} /> : null}
              </Box>
            }
            label={
              <IconLabel>
                <Tooltip title="Expected start date">
                  <Box display="flex" alignItems="center">
                    <CalendarMonth
                      sx={{ color: isExpectedStartDateWarning ? 'error.main' : 'text.secondary', fontSize: 20 }}
                    />
                  </Box>
                </Tooltip>
              </IconLabel>
            }
            options={PROJECT_TEAM_TYPE_LABELS}
          />
        </IconSectionRow>
      </Grid>
    </Grid>
  );
};

export default OpportunityDashboardGeneralInfo;
